import styled, { css, keyframes } from 'styled-components';

import { SLOT, SLOT_ANIM } from '../../../../theme/tokens';

/* Reel geometry. One symbol is visible per reel (the payline); the strip is a tall
 * column the spin scrolls through. The result symbol is the LAST cell, so the
 * resting transform (and the reduced-motion fallback) shows the outcome with no
 * animation. */
const CELL = 64; // px per symbol cell (== visible reel height)
const STRIP_LEN = 22; // cells per reel (the spin distance)
const TRAVEL = (STRIP_LEN - 1) * CELL; // upward travel that lands the last cell

/* Reels stop left → right: each later reel keeps spinning a beat longer. */
const REEL2_MS = SLOT_ANIM.spinMs + SLOT_ANIM.reelStaggerMs;
const REEL3_MS = SLOT_ANIM.spinMs + 2 * SLOT_ANIM.reelStaggerMs;
/* The win pulse / lose shudder begins once the LAST reel has landed. */
const SETTLE_DELAY = REEL3_MS;

/* The whirl: scroll the strip up through its symbols, blurred mid-flight, easing
 * to a sharp stop on the result cell. */
const spin = keyframes`
  0%   { transform: translateY(0); filter: blur(0); }
  8%   { filter: blur(2px); }
  86%  { filter: blur(0.6px); }
  100% { transform: translateY(-${TRAVEL}px); filter: blur(0); }
`;

/* Gentle entrance for the whole cabinet. */
const cardIn = keyframes`
  from { opacity: 0; transform: translateY(6px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

/* Win: a gold ring around the reel window that breathes a couple of times. */
const winPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 1px ${SLOT.winRing}, 0 0 14px 1px ${SLOT.winGlow}; }
  50%      { box-shadow: 0 0 0 2px ${SLOT.winRing}, 0 0 30px 7px ${SLOT.winGlow}; }
`;

/* Lose: the reels thunk home with a short side-to-side shudder. */
const loseShake = keyframes`
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-3px); }
  40% { transform: translateX(3px); }
  60% { transform: translateX(-2px); }
  80% { transform: translateX(2px); }
`;

/* One symbol cell — emoji or the styled "7". */
export const Cell = styled.div`
  height: ${CELL}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Glyph = styled.span`
  font-size: 30px;
  line-height: 1;
`;

export const Seven = styled.span`
  font-family: 'Arial Black', Impact, system-ui, sans-serif;
  font-weight: 900;
  font-size: 34px;
  line-height: 1;
  color: ${SLOT.seven};
`;

/* The scrolling column. Rests showing the last (result) cell; the spin animates
 * from the top down to it. */
export const Strip = styled.div`
  display: flex;
  flex-direction: column;
  transform: translateY(-${TRAVEL}px);
  animation: ${spin} ${SLOT_ANIM.spinMs}ms ${SLOT_ANIM.spinEase} both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* A single reel: white mechanical glass, clipped, with the symbols fading into the
 * glass at the top and bottom edges. */
export const Reel = styled.div`
  position: relative;
  width: 56px;
  height: ${CELL}px;
  overflow: hidden;
  border-radius: 8px;
  background: ${SLOT.reelBg};
  border: 1px solid ${SLOT.reelBorder};
  box-shadow: ${SLOT.reelInset};

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 15px;
    z-index: 1;
    pointer-events: none;
  }
  &::before {
    top: 0;
    background: ${SLOT.reelFadeTop};
  }
  &::after {
    bottom: 0;
    background: ${SLOT.reelFadeBottom};
  }

  &:nth-child(2) ${Strip} {
    animation-duration: ${REEL2_MS}ms;
  }
  &:nth-child(3) ${Strip} {
    animation-duration: ${REEL3_MS}ms;
  }
`;

export const Reels = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

/* Payline marker arrows pointing at the centre row from each side. */
export const Payline = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
  }
  &::before {
    left: 2px;
    border-left: 7px solid ${SLOT.paylineMarker};
  }
  &::after {
    right: 2px;
    border-right: 7px solid ${SLOT.paylineMarker};
  }
`;

/* The reel window — the recessed glass area inside the cabinet that holds the
 * reels. On a win it wears a pulsing gold ring; on a loss it shudders. */
export const Window = styled.div<{ $result: 'win' | 'lose' }>`
  position: relative;
  display: flex;
  padding: 8px 14px;
  border-radius: 12px;

  ${(p) =>
    p.$result === 'win'
      ? css`
          box-shadow: 0 0 0 1px ${SLOT.winRing}, 0 0 14px 1px ${SLOT.winGlow};
          animation: ${winPulse} ${SLOT_ANIM.winPulseMs}ms ease-in-out ${SETTLE_DELAY}ms 3;
        `
      : css`
          box-shadow: 0 0 0 1px ${SLOT.loseRing};
          animation: ${loseShake} ${SLOT_ANIM.loseShakeMs}ms ease-in-out ${SETTLE_DELAY}ms 1 both;
        `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* The cabinet — the retro casino frame: warm body, gold marquee, soft drop. */
export const Cabinet = styled.div<{ $result: 'win' | 'lose' }>`
  width: fit-content;
  max-width: 100%;
  margin: 2px 0;
  padding: 12px 14px;
  border-radius: 16px;
  background: ${SLOT.cabinet};
  border: 1px solid ${SLOT.cabinetBorder};
  box-shadow: ${SLOT.cabinetShadow};
  animation: ${cardIn} ${SLOT_ANIM.cardInMs}ms ${SLOT_ANIM.spinEase} both;

  ${(p) =>
    p.$result === 'win' &&
    css`
      ${Seven} {
        filter: drop-shadow(0 0 5px ${SLOT.winGlow});
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/* The lit marquee strip across the top of the cabinet. */
export const Marquee = styled.div`
  height: 6px;
  margin-bottom: 10px;
  border-radius: 6px;
  background: ${SLOT.marquee};
`;
